import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Badge, Button, Modal, Input, Spinner } from '@components/ui';
import { useSprints, useCreateSprint, useUpdateSprint, useDeleteSprint } from '@hooks';
import { useUIStore } from '@store';

const statusColors = {
  PLANNED: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Planificación' },
  ACTIVE: { bg: 'bg-emerald-100', text: 'text-emerald-600', label: 'Activo' },
  COMPLETED: { bg: 'bg-blue-100', text: 'text-blue-600', label: 'Completado' },
};

// Formatear fecha para mostrar sin desfase de zona horaria
const formatDateDisplay = (dateValue) => {
  if (!dateValue) return '';
  // Extraer la fecha del string ISO directamente
  const dateStr = typeof dateValue === 'string' && dateValue.includes('T')
    ? dateValue.split('T')[0]
    : dateValue;
  // Parsear como fecha local (YYYY-MM-DD)
  const [year, month, day] = dateStr.split('-');
  return `${parseInt(day)}/${parseInt(month)}/${year}`;
};

function SprintCard({ sprint, onEdit, onDelete, onClick }) {
  const status = statusColors[sprint.status] || statusColors.PLANNED;
  const totalPoints = sprint.metrics?.totalPoints || sprint.tasks?.reduce((sum, t) => sum + (t.points || 0), 0) || 0;
  const completedPoints = sprint.metrics?.donePoints || sprint.tasks?.filter(t => t.status === 'done').reduce((sum, t) => sum + (t.points || 0), 0) || 0;
  const progress = sprint.metrics?.progress || (totalPoints > 0 ? Math.round((completedPoints / totalPoints) * 100) : 0);

  const hasAnyDate = sprint.startDate || sprint.endDate;
  const isCalculatedDates = hasAnyDate && sprint.hasExplicitDates === false;

  return (
    <Card className="hover:shadow-card-hover transition-all cursor-pointer" onClick={() => onClick?.(sprint)}>
      <div className="flex justify-between items-start mb-3">
        <div>
          <h3 className="font-semibold text-slate-800">{sprint.name}</h3>
          {hasAnyDate ? (
            <p className="text-xs text-slate-500 mt-0.5">
              {sprint.startDate && formatDateDisplay(sprint.startDate)} - {sprint.endDate && formatDateDisplay(sprint.endDate)}
              {isCalculatedDates && <span className="ml-1 text-slate-400">(auto)</span>}
            </p>
          ) : (
            <p className="text-xs text-slate-400 mt-0.5 italic">
              Fechas se calculan de las tareas
            </p>
          )}
        </div>
        <Badge className={`${status.bg} ${status.text}`}>{status.label}</Badge>
      </div>

      {sprint.goal && (
        <p className="text-sm text-slate-600 mb-3">{sprint.goal}</p>
      )}

      <div className="mb-3">
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>Progreso</span>
          <span>{completedPoints} / {totalPoints} pts ({progress}%)</span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <div
            className="bg-indigo-500 h-2 rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-500">
          📋 {sprint.metrics?.totalTasks || sprint.tasks?.length || 0} tareas
        </span>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onEdit(sprint); }}>
            Editar
          </Button>
          <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); onDelete(sprint); }}>
            Eliminar
          </Button>
        </div>
      </div>
    </Card>
  );
}

SprintCard.propTypes = {
  sprint: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClick: PropTypes.func,
};

export default function SprintsView({ projectId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSprint, setEditingSprint] = useState(null);
  const [deletingSprint, setDeletingSprint] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    goal: '',
    startDate: '',
    endDate: '',
  });

  const setSelectedSprintId = useUIStore((state) => state.setSelectedSprintId);
  const setActiveView = useUIStore((state) => state.setActiveView);

  const { data: sprintsData, isLoading } = useSprints({ projectId });
  const createSprint = useCreateSprint();
  const updateSprint = useUpdateSprint();
  const deleteSprint = useDeleteSprint();

  const sprints = sprintsData?.data || [];

  const formatDateForInput = (dateValue) => {
    if (!dateValue) return '';
    try {
      // Si es un string ISO, extraer directamente la fecha sin conversión de zona horaria
      if (typeof dateValue === 'string' && dateValue.includes('T')) {
        return dateValue.split('T')[0];
      }
      // Si es un string de fecha simple, devolverlo
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      // Fallback: convertir a ISO y extraer fecha
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const handleOpenModal = (sprint = null) => {
    if (sprint) {
      setEditingSprint(sprint);
      setFormData({
        name: sprint.name || '',
        goal: sprint.goal || '',
        startDate: formatDateForInput(sprint.startDate),
        endDate: formatDateForInput(sprint.endDate),
      });
    } else {
      setEditingSprint(null);
      setFormData({ name: '', goal: '', startDate: '', endDate: '' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSprint(null);
    setFormData({ name: '', goal: '', startDate: '', endDate: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      projectId,
      startDate: formData.startDate || null,
      endDate: formData.endDate || null,
    };

    if (editingSprint) {
      await updateSprint.mutateAsync({ id: editingSprint.id, data });
    } else {
      await createSprint.mutateAsync(data);
    }
    handleCloseModal();
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDeleteClick = (sprint) => {
    setDeletingSprint(sprint);
  };

  const handleConfirmDelete = async () => {
    if (deletingSprint) {
      await deleteSprint.mutateAsync(deletingSprint.id);
      setDeletingSprint(null);
    }
  };

  const handleCancelDelete = () => {
    setDeletingSprint(null);
  };

  const handleSprintClick = (sprint) => {
    setSelectedSprintId(sprint.id);
    setActiveView('sprint-detail');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  const activeSprints = sprints.filter(s => s.status === 'ACTIVE');
  const planningSprints = sprints.filter(s => s.status === 'PLANNED');
  const completedSprints = sprints.filter(s => s.status === 'COMPLETED');

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-xl font-bold text-slate-800">Sprints</h1>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          + Nuevo Sprint
        </Button>
      </div>

      {activeSprints.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
            Sprint Activo
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeSprints.map((sprint) => (
              <SprintCard key={sprint.id} sprint={sprint} onEdit={handleOpenModal} onDelete={handleDeleteClick} onClick={handleSprintClick} />
            ))}
          </div>
        </div>
      )}

      {planningSprints.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
            En Planificación ({planningSprints.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {planningSprints.map((sprint) => (
              <SprintCard key={sprint.id} sprint={sprint} onEdit={handleOpenModal} onDelete={handleDeleteClick} onClick={handleSprintClick} />
            ))}
          </div>
        </div>
      )}

      {completedSprints.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
            Completados ({completedSprints.length})
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {completedSprints.map((sprint) => (
              <SprintCard key={sprint.id} sprint={sprint} onEdit={handleOpenModal} onDelete={handleDeleteClick} onClick={handleSprintClick} />
            ))}
          </div>
        </div>
      )}

      {sprints.length === 0 && (
        <Card className="text-center py-12">
          <p className="text-slate-500 mb-4">No hay sprints creados</p>
          <Button variant="primary" onClick={() => handleOpenModal()}>
            Crear primer sprint
          </Button>
        </Card>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingSprint ? 'Editar Sprint' : 'Nuevo Sprint'}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={createSprint.isPending || updateSprint.isPending}
            >
              {editingSprint ? 'Guardar' : 'Crear Sprint'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleSubmit} className="space-y-5">
          <Input
            label="Nombre del Sprint"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Ej: Sprint 1"
            required
          />
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Objetivo del Sprint
            </label>
            <textarea
              name="goal"
              value={formData.goal}
              onChange={handleChange}
              placeholder="¿Qué queremos lograr en este sprint?"
              rows={2}
              className="input resize-none"
            />
          </div>
          <div>
            <p className="text-xs text-slate-500 mb-2">
              Las fechas son opcionales. Si no se especifican, se calcularán automáticamente basándose en las tareas del sprint.
            </p>
            <div className="grid grid-cols-2 gap-5">
              <Input
                label="Fecha de Inicio (opcional)"
                name="startDate"
                type="date"
                value={formData.startDate}
                onChange={handleChange}
              />
              <Input
                label="Fecha de Fin (opcional)"
                name="endDate"
                type="date"
                value={formData.endDate}
                onChange={handleChange}
                min={formData.startDate || undefined}
              />
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={!!deletingSprint}
        onClose={handleCancelDelete}
        title="Eliminar Sprint"
        footer={
          <>
            <Button variant="secondary" onClick={handleCancelDelete}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleConfirmDelete}
              loading={deleteSprint.isPending}
            >
              Eliminar
            </Button>
          </>
        }
      >
        <p className="text-slate-600">
          ¿Estás seguro de que deseas eliminar el sprint <strong>{deletingSprint?.name}</strong>?
        </p>
        <p className="text-sm text-slate-500 mt-2">
          Las tareas del sprint se moverán al backlog.
        </p>
      </Modal>
    </div>
  );
}

SprintsView.propTypes = {
  projectId: PropTypes.number,
};
